import { Printer, TriangleAlert } from "lucide-react";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	currentDriverOption,
	GENERIC_DRIVER_NOTE,
	IMMEDIATE_DIALOG_NOTE,
	isGenericDriver,
	jobsCountLabel,
	jobsSummary,
	networkPrinterAddress,
	PLAN_DIALOG_NOTE,
	portKindLabel,
	printerReinstallable,
	reinstallBlocker,
	reinstallDriverOptions,
} from "./printer-labels";
import type { PlanAction, PlanNames } from "./printer-plan";
import type { PrinterDevice, PrinterItem } from "./printers-api";

export type FixTarget = { device: PrinterDevice; printer: PrinterItem };

/** Stan kolejki w oknie: problemy (offline, pauza, zadania, błąd zadania) albo „bez problemu”. */
function stateLines(printer: PrinterItem): { text: string; problem: boolean }[] {
	const { jobs } = printer;
	const lines: { text: string; problem: boolean }[] = [];
	if (printer.offline) {
		lines.push({ text: "Wyłączona — Windows używa jej w trybie offline.", problem: true });
	}
	if (printer.paused) {
		lines.push({ text: "Wstrzymana — kolejka nie wysyła zadań do drukarki.", problem: true });
	}
	lines.push({ text: jobsSummary(jobs), problem: jobs != null && jobs.count > 0 });
	if (jobs?.error) {
		lines.push({
			text: "Zadanie w kolejce stoi z błędem (np. brak papieru, zacięcie albo drukarka niedostępna).",
			problem: true,
		});
	}
	if (!lines.some((line) => line.problem)) {
		lines.push({ text: "Windows nie zgłasza problemu z tą kolejką.", problem: false });
	}
	return lines;
}

/**
 * „Napraw drukarkę”: stan kolejki, „Wznów” i „Wyczyść kolejkę” zaznaczone wg
 * stanu (operacja `fix_printer`), a przy kolejce sieciowej TCP/IP osobno
 * „Zainstaluj ponownie” z wyborem sterownika (`reinstall_printer`). Okno
 * dopisuje wybraną naprawę do planu; czyszczenie kolejki i reinstalację plan
 * potwierdza jeszcze raz przed wykonaniem.
 */
export function FixPrinterDialog({
	target,
	onPlan,
	onClose,
	immediate = false,
}: {
	target: FixTarget | null;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	/** Panel helpdesku: przyciski startują operację od razu, bez planu. */
	immediate?: boolean;
}) {
	return (
		<Dialog open={target != null} onOpenChange={(next) => (next ? undefined : onClose())}>
			<DialogContent className="max-w-lg">
				{target ? (
					// Inna kolejka = świeże zaznaczenia.
					<FixPrinterBody
						key={`${target.device.deviceUuid}:${target.printer.name}`}
						target={target}
						onPlan={onPlan}
						onClose={onClose}
						immediate={immediate}
					/>
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function FixPrinterBody({
	target,
	onPlan,
	onClose,
	immediate,
}: {
	target: FixTarget;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	immediate: boolean;
}) {
	const ids = useId();
	const { device, printer } = target;
	const { jobs } = printer;
	// Na start zaznaczone to, co pasuje do stanu kolejki.
	const [resume, setResume] = useState(() => printer.offline || printer.paused);
	const [purge, setPurge] = useState(() => jobs != null && (jobs.error || jobs.count > 0));
	const drivers = reinstallDriverOptions(device, printer);
	const currentDriver = currentDriverOption(drivers, printer);
	const [driverName, setDriverName] = useState(() => currentDriver ?? "");
	const reinstallAddress = printerReinstallable(printer) ? printer.address : null;
	const blocker = reinstallAddress ? reinstallBlocker(device, printer) : null;
	// Na udostępnionej drukarce w kolejce czekają też wydruki innych stanowisk.
	const sharedQueue = printer.managedShare != null || printer.usedBy.length > 0;
	const names: PlanNames = { device: device.displayName, printer: printer.name };

	const submitFix = () => {
		if (!resume && !purge) return;
		onPlan(
			{
				kind: "fix_printer",
				input: { deviceUuid: device.deviceUuid, printerName: printer.name, purge, resume },
			},
			names,
		);
		onClose();
	};

	const submitReinstall = () => {
		if (!driverName || blocker) return;
		onPlan(
			{
				kind: "reinstall_printer",
				input: { deviceUuid: device.deviceUuid, printerName: printer.name, driverName },
			},
			{ ...names, extra: driverName },
		);
		onClose();
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>Napraw drukarkę</DialogTitle>
				<DialogDescription>Kolejka drukarki na komputerze {device.displayName}.</DialogDescription>
			</DialogHeader>
			<DialogBody className="space-y-4">
				<div className="flex items-start gap-2 rounded-md border bg-muted/40 p-2 text-sm">
					<Printer className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
					<div className="min-w-0">
						<p className="font-medium break-words">{printer.name}</p>
						<p className="text-xs text-muted-foreground">
							{portKindLabel(printer.portKind)}
							{printer.port ? ` (${printer.port})` : ""}
							{printer.driver ? ` · sterownik ${printer.driver}` : ""}
						</p>
						<ul aria-label="Stan kolejki" className="mt-1 space-y-0.5 text-xs">
							{stateLines(printer).map((line) => (
								<li
									key={line.text}
									className={line.problem ? "text-status-warning-text" : "text-muted-foreground"}
								>
									{line.text}
								</li>
							))}
						</ul>
					</div>
				</div>

				<fieldset className="space-y-2">
					<legend className="mb-1 text-sm font-medium">Napraw kolejkę</legend>
					<div>
						<label className="flex items-center gap-2 text-sm">
							<input
								type="checkbox"
								checked={resume}
								onChange={(e) => setResume(e.target.checked)}
								aria-describedby={`${ids}-resume`}
								className="size-4 shrink-0 accent-primary"
							/>
							Wznów
						</label>
						<p id={`${ids}-resume`} className="pl-6 text-xs text-muted-foreground">
							Zdejmuje tryb offline i wstrzymanie kolejki — niczego nie usuwa.
						</p>
					</div>
					<div>
						<label className="flex items-center gap-2 text-sm">
							<input
								type="checkbox"
								checked={purge}
								onChange={(e) => setPurge(e.target.checked)}
								aria-describedby={`${ids}-purge`}
								className="size-4 shrink-0 accent-primary"
							/>
							Wyczyść kolejkę
						</label>
						<p id={`${ids}-purge`} className="pl-6 text-xs text-muted-foreground">
							Usuwa zadania tylko tej drukarki — pomaga, gdy zawieszony wydruk blokuje kolejne.
						</p>
						{purge ? (
							<p
								className={cn(
									"mt-1 ml-6 flex items-start gap-2 rounded-md border p-2 text-xs",
									statusClasses.warning,
								)}
							>
								<TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
								<span>
									Oczekujące wydruki tej drukarki zostaną usunięte
									{jobs && jobs.count > 0 ? ` (${jobsCountLabel(jobs.count)})` : ""}
									{sharedQueue ? ", także wysłane z innych stanowisk" : ""} — trzeba je będzie
									wydrukować ponownie.
								</span>
							</p>
						) : null}
					</div>
					{!resume && !purge ? (
						<p className="text-xs text-muted-foreground">
							Zaznacz, co zrobić z kolejką, żeby ją naprawić.
						</p>
					) : null}
				</fieldset>

				{reinstallAddress ? (
					<section aria-labelledby={`${ids}-reinstall`} className="space-y-2 rounded-md border p-3">
						<h3 id={`${ids}-reinstall`} className="text-sm font-medium">
							Zainstaluj ponownie
						</h3>
						<p className="text-xs text-muted-foreground">
							Usuniemy kolejkę i założymy ją od nowa pod tą samą nazwą i z tym samym portem (
							<span className="font-mono">{networkPrinterAddress(reinstallAddress)}</span>). Pomaga,
							gdy kolejka jest uszkodzona albo trzeba zmienić sterownik.
						</p>
						<div className="space-y-1">
							<label htmlFor={`${ids}-driver`} className="text-sm font-medium">
								Sterownik
							</label>
							<select
								id={`${ids}-driver`}
								value={driverName}
								onChange={(e) => setDriverName(e.target.value)}
								disabled={blocker != null}
								className="h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm disabled:opacity-50"
							>
								{currentDriver == null ? <option value="">Wybierz sterownik…</option> : null}
								{drivers.map((name) => (
									<option key={name} value={name}>
										{name === currentDriver ? `${name} (obecny)` : name}
									</option>
								))}
							</select>
							{driverName && isGenericDriver(driverName) ? (
								<p className="text-xs text-muted-foreground">{GENERIC_DRIVER_NOTE}</p>
							) : null}
						</div>
						<p
							className={cn(
								"flex items-start gap-2 rounded-md border p-2 text-xs",
								statusClasses.warning,
							)}
						>
							<TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
							<span>Ustawienia kolejki (preferencje wydruku) i jej zadania przepadną.</span>
						</p>
						<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
							<Button
								size="sm"
								variant="outline"
								className="h-7 text-xs max-md:h-9"
								disabled={blocker != null || !driverName}
								aria-describedby={blocker ? `${ids}-blocker` : undefined}
								onClick={submitReinstall}
							>
								{immediate ? "Zainstaluj ponownie" : "Dodaj ponowną instalację do planu"}
							</Button>
							{/* Powód blokady jako widoczny tekst: title na wyłączonym Button się nie pokazuje. */}
							{blocker ? (
								<span id={`${ids}-blocker`} className="text-xs text-muted-foreground">
									{blocker}
								</span>
							) : null}
						</div>
					</section>
				) : null}
				<p className="text-xs text-muted-foreground">
					{immediate ? IMMEDIATE_DIALOG_NOTE : PLAN_DIALOG_NOTE}
				</p>
			</DialogBody>
			<DialogFooter>
				<Button variant="outline" size="sm" onClick={onClose}>
					Anuluj
				</Button>
				<Button size="sm" onClick={submitFix} disabled={!resume && !purge}>
					{immediate ? "Napraw" : "Dodaj naprawę do planu"}
				</Button>
			</DialogFooter>
		</>
	);
}
